import { useState, useCallback, useEffect } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@component/dialog";
import { Button } from "@component/button";
import { Input } from "@component/input";
import { Badge } from "@component/badge";
import { UploadIcon, XIcon, FileTextIcon } from "lucide-react";
import { Job } from "@type/job";
import { useBulkUploadCVContext, saveBulkUploadState } from "@context/bulk_upload_cv_context";
import { getListCVs, bulkUploadCVQueue } from "@service/cv";
import { useSubscription } from "@context/subscription_context";
import { useSnackbar } from "@hook/use-snackbar";
import UploadProcessConfirmationDialog from "./dialog-confirm-process-upload";

interface Props {
    isOpen: boolean;
    onClose: () => void;
    job: Job;
}

export default function BulkCVUploadDialog({
    isOpen,
    onClose,
    job,
}: Props) {
    const { setJobId, setFileTrackerMap, setBatchId, setIsVisible, setIsProcessing } = useBulkUploadCVContext();
    const { showTrialUsageLimitDialog } = useSubscription();
    const snackbar = useSnackbar();
    const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
    const [isLoading, setLoading] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [existingCVFileNames, setExistingCVFileNames] = useState<string[]>([]);

    useEffect(() => {
        if (!isOpen) {
            setUploadedFiles([]);
            setUploadError(null);
            setShowConfirmation(false);
        } else {
            setLoading(true);
            getListCVs(job.id).then((resp) => {
                if (resp.status === 200) {
                    setExistingCVFileNames(resp.data.data);
                }
            }).finally(() => {
                setLoading(false);
            });
        }
    }, [isOpen])

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        const files = Array.from(e.dataTransfer.files);
        handleFiles(files);
    }, []);

    const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const files = Array.from(e.target.files);
            handleFiles(files);
        }
    };

    const handleFiles = (files: File[]) => {
        // Clear any previous errors
        setUploadError(null);

        // Check if adding these files would exceed the 20-file limit
        const totalFiles = uploadedFiles.length + files.length;
        if (totalFiles > 50) {
            setUploadError(
                `You can only upload up to 50 files at a time. You currently have ${uploadedFiles.length} file${uploadedFiles.length !== 1 ? "s" : ""} and are trying to add ${files.length} more.`
            );
            return;
        }

        const duplicatesInSession: string[] = [];
        const filesAfterUploadCheck: string[] = [];
        const supportFiles: File[] = [];
        const existingFileNames = new Set(uploadedFiles.map((f) => f.name));
        const existingProfileFiles = new Set(existingCVFileNames);

        files.forEach((file) => {
            if (existingFileNames.has(file.name)) {
                duplicatesInSession.push(file.name);
            } else if (existingProfileFiles.has(file.name)) {
                filesAfterUploadCheck.push(file.name);
            } else {
                supportFiles.push(file);
            }
        });

        const errorMessages: string[] = [];
        if (duplicatesInSession.length > 0) {
            errorMessages.push(
                `${duplicatesInSession.length} file${duplicatesInSession.length !== 1 ? "s" : ""} already added to the current list of uploads`
            );
        }

        if (filesAfterUploadCheck.length > 0) {
            errorMessages.push(
                `${filesAfterUploadCheck.length} file${filesAfterUploadCheck.length !== 1 ? "s" : ""} already exist in the system (detected via email)`
            );
        }

        if (errorMessages.length > 0) {
            const combinedError = errorMessages.join("; ") + ".";
            setUploadError(combinedError);
        }

        setUploadedFiles((prev) => [...prev, ...supportFiles]);
    };

    const removeFile = (fIndex: number) => {
        setUploadedFiles((prevFiles) => {
            const newFiles = [...prevFiles];
            newFiles.splice(fIndex, 1);
            return newFiles;
        });
    };

    const handleUploadClick = () => {
        // Show confirmation dialog
        setShowConfirmation(true);
    };

    const startProcessing = () => {
        // Snapshot the files before handleClose() resets the dialog state below.
        const filesToQueue = uploadedFiles;
        const fileCount = filesToQueue.length;

        // Fire-and-forget: do NOT block the UI waiting for the queue response.
        // We close the dialog and show the toast immediately; the progress tracker
        // (and error handling) is wired up when the response eventually arrives.
        bulkUploadCVQueue(job.id, filesToQueue)
            .then((resp) => {
                const data = resp.data?.data;

                // Build temp_cv_id → filename mapping from sent_messages
                const trackers = (data?.sent_messages || []).map((m: { filename: string; temp_cv_id: string }) => ({
                    temp_cv_id: m.temp_cv_id,
                    filename: m.filename,
                }));

                setJobId(job.id);
                setFileTrackerMap(trackers);
                setBatchId(data?.batch_id || null);
                setIsProcessing(true);
                setIsVisible(true);

                // Persist to localStorage so progress survives page refresh
                saveBulkUploadState({
                    jobId: job.id,
                    batchId: data?.batch_id || "",
                    fileTrackerMap: trackers,
                });
            })
            .catch((error: unknown) => {
                const err = error as { response?: { status?: number; data?: { is_usage_limit_reached?: boolean; is_trial_end_soon_usage_reached?: boolean } } };
                if (err.response?.status === 400 && (err.response?.data?.is_usage_limit_reached || err.response?.data?.is_trial_end_soon_usage_reached)) {
                    showTrialUsageLimitDialog(!!err.response?.data?.is_trial_end_soon_usage_reached);
                } else {
                    snackbar.showError(
                        "Bulk upload failed",
                        "Could not queue your CVs for processing. Please try again.",
                    );
                }
            });

        // Don't wait for the request — close the dialog and show the toast now.
        handleClose();
        snackbar.showSuccess(
            "Bulk upload started",
            `Processing ${fileCount} CV${fileCount === 1 ? "" : "s"} in the background — progress will appear shortly.`,
        );
    };

    // Removed all candidate management and email functions - moved to uploaded-candidates-dialog

    const handleClose = () => {
        setUploadedFiles([]);
        setUploadError(null);
        setShowConfirmation(false);
        onClose();
    };

    // Removed invite step logic

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="max-w-[85vw] w-[85vw] h-[90vh] overflow-hidden flex flex-col">
                <DialogHeader>
                    <div className="flex items-center justify-between pr-8">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center">
                                <UploadIcon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                            </div>
                            <div>
                                <DialogTitle className="text-lg font-semibold text-foreground">
                                    Bulk Upload CVs - {job.job_title}
                                </DialogTitle>
                            </div>
                        </div>
                    </div>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto">
                    {/* Upload - 2 Panel Layout */}
                    {
                        <div
                            className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-full"
                            style={{
                                paddingTop: "16px",
                                paddingRight: "16px",
                                paddingBottom: "16px",
                                paddingLeft: "16px",
                            }}
                        >
                            {/* Left Panel - Upload Area (Static) */}
                            <div className="flex flex-col">
                                <div
                                    onDragOver={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                    onDrop={handleDrop}
                                    className={`border-2 border-dashed rounded-lg p-8 flex-1 flex flex-col items-center justify-center text-center transition-colors ${isDragging
                                        ? "border-primary bg-primary/5"
                                        : "border-border hover:border-blue-500 dark:hover:border-blue-400"
                                        }`}
                                >
                                    <UploadIcon className="w-16 h-16 mb-4 text-muted-foreground" />

                                    <h3 className="text-xl font-semibold mb-2">
                                        Drag & drop CVs here
                                    </h3>
                                    <p className="text-sm text-muted-foreground mb-6">
                                        or click to browse files (PDF, DOC, DOCX)
                                    </p>
                                    <Input
                                        type="file"
                                        multiple
                                        accept=".pdf,.doc,.docx"
                                        onChange={handleFileInput}
                                        className="hidden"
                                        id="file-upload"
                                    />

                                    <label htmlFor="file-upload">
                                        <Button variant="outline" size="lg" asChild disabled={isLoading}>
                                            <span
                                                style={{ paddingRight: "16px", paddingLeft: "16px" }}
                                            >
                                                <UploadIcon className="w-4 h-4 mr-2" />
                                                Browse Files
                                            </span>
                                        </Button>
                                    </label>

                                    {/* File limit information */}
                                    <p className="text-xs text-muted-foreground mt-4">
                                        Maximum 50 files per upload
                                    </p>

                                    {/* Error message */}
                                    {uploadError && (
                                        <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-lg">
                                            <p className="text-xs text-red-600 dark:text-red-400">
                                                {uploadError}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Right Panel - Uploaded Files List (Scrollable) */}
                            <div className="flex flex-col min-h-0">
                                <div className="flex items-center justify-between mb-3 flex-shrink-0">
                                    <h4 className="font-semibold text-lg">Uploaded Files</h4>
                                    {uploadedFiles.length > 0 && (
                                        <Badge
                                            variant="secondary"
                                            className="font-semibold bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200 border-blue-200 dark:border-blue-800 py-1 px-2.5 text-sm rounded-2xl"
                                        >
                                            {uploadedFiles.length} file
                                            {uploadedFiles.length !== 1 ? "s" : ""}
                                        </Badge>
                                    )}
                                </div>

                                {uploadedFiles.length === 0 ? (
                                    <div className="flex-1 flex items-center justify-center rounded-lg p-8">
                                        <div className="text-center">
                                            <FileTextIcon className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />

                                            <p className="text-sm text-muted-foreground">
                                                No files uploaded yet.
                                            </p>
                                            <p className="text-xs text-muted-foreground mt-1">
                                                Upload files to see them here.
                                            </p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex-1 overflow-y-auto min-h-0 border border-border rounded-lg">
                                        <div className="space-y-0">
                                            {uploadedFiles.map((file, index) => (
                                                <div
                                                    key={index}
                                                    className={`flex items-center justify-between p-3 hover:bg-muted/50 transition-colors ${index !== uploadedFiles.length - 1 ? "border-b" : ""
                                                        }`}
                                                >
                                                    <div className="flex items-center gap-3 flex-1 min-w-0">
                                                        <FileTextIcon className="w-5 h-5 text-muted-foreground flex-shrink-0" />

                                                        <div className="flex-1 min-w-0">
                                                            <p
                                                                className="text-sm font-medium truncate"
                                                                title={file.name}
                                                            >
                                                                {file.name}
                                                            </p>
                                                            <p className="text-xs text-muted-foreground">
                                                                {(file.size / 1024).toFixed(1)} KB
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => removeFile(index)}
                                                        className="flex-shrink-0 ml-2"
                                                    >
                                                        <XIcon className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    }
                </div>

                <div className="flex items-center justify-between pt-3 gap-4">
                    <div className="flex gap-2 ml-auto">
                        <Button
                            onClick={handleUploadClick}
                            disabled={uploadedFiles.length === 0 || isLoading}
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                        >
                            {isLoading ? "Processing..." : `Upload & Process CVs (${uploadedFiles.length})`}
                        </Button>
                    </div>
                </div>
            </DialogContent>

            {/* Confirmation Dialog */}
            <UploadProcessConfirmationDialog
                isOpen={showConfirmation}
                onClose={() => setShowConfirmation(false)}
                onConfirm={startProcessing}
                fileCount={uploadedFiles.length}
            />
        </Dialog>
    );
}
